import React, {useState} from 'react';
import TextBox from '../../TextBox';
import styled from 'styled-components/native';
import Value from './Value';
import Confirmation, {
  getConfirmationPrompt,
  isNoneValue,
} from '../confirmation';
import {ParameterTypeProps} from '..';
import colors from '../../../constants/colors';

const Prompt = styled(TextBox)`
  padding-top: 20px;
  font-size: 18px;
  margin-bottom: 1px;
  background: ${colors.app.white};
`;

const Description = styled(TextBox)`
  background: ${colors.app.white};
  padding-bottom: 0;
`;

export default function Select({
  parameter,
  storeReportParameterValue,
  nextParameter,
  reportParameter,
}: ParameterTypeProps) {
  // Tracks the user's answer to the follow-up verification question:
  // null = unanswered, true = confirmed correct, false = wants to change it.
  const [confirmed, setConfirmed] = useState<boolean | null>(null);

  const confirmationPrompt = getConfirmationPrompt(parameter);

  // The currently selected value (Select stores a single value), used to decide
  // whether a verification question is required before advancing.
  const selectedValue = reportParameter?.reportParameterValues?.[0];
  const selectedParameterValue = parameter.parameterValues.find(
    value => value.id == selectedValue?.parameterValueId,
  );
  const requiresConfirmation =
    !!confirmationPrompt &&
    !!selectedParameterValue &&
    !isNoneValue(selectedParameterValue);

  const nextParameterIdFor = (parameterValue: {
    nextParameterByValue?: {nextParameter: {id: string}} | null;
  }) =>
    parameterValue.nextParameterByValue?.nextParameter.id ??
    parameter.nextParameter?.id;

  return (
    <>
      {parameter.parameterCategory ? (
        <Description>{parameter.parameterCategory.name}</Description>
      ) : null}
      {parameter.description ? (
        <Description>{parameter.description}</Description>
      ) : null}
      <Prompt bold={true}>{parameter.prompt ?? parameter.name}</Prompt>
      {[...parameter.parameterValues]
        .sort((a, b) => Number(a.id) - Number(b.id))
        .map(parameterValue => (
        <Value
          checked={
            !!(
              reportParameter &&
              reportParameter.reportParameterValues &&
              reportParameter.reportParameterValues.find(
                value =>
                  value.parameterValueId &&
                  value.parameterValueId === parameterValue.id,
              )
            )
          }
          parameterValue={parameterValue}
          key={parameterValue.id}
          onPress={() => {
            const nextParameterId = nextParameterIdFor(parameterValue);

            storeReportParameterValue({
              parameterId: parameter.id,
              nextParameterId,
              reportParameterValues: [
                {
                  parameterValueId: parameterValue.id,
                  value: parameterValue.value,
                },
              ],
            });

            // Non-"none" answers to a verification parameter pause here so the
            // user can confirm before advancing; everything else advances now.
            if (!!confirmationPrompt && !isNoneValue(parameterValue)) {
              setConfirmed(null);
              return;
            }

            nextParameter(nextParameterId);
          }}
        />
        ))}
      {requiresConfirmation ? (
        <Confirmation
          prompt={confirmationPrompt!}
          confirmed={confirmed}
          onConfirm={value => {
            setConfirmed(value);
            if (value && selectedParameterValue) {
              nextParameter(nextParameterIdFor(selectedParameterValue));
            }
          }}
        />
      ) : null}
    </>
  );
}
